import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IWWPartners } from 'legacy/lut/classes/models/partner360/IWWPartners'
import { PartnerType } from 'legacy/lut/classes/models/PartnerType'
import {
  IComposedReportFilters,
  ScopeSelectKeys,
} from 'legacy/lut/components/widgets/public/partner360/composedReport'

import { initialSelectsData, initialState } from './configs'
import { PARTNER360_COMPOSED_REPORT_NAMESPACE } from './constants'
import { removeDuplicates } from './helpers/removeDuplicates'
import {
  IComposedFilterForReport,
  IComposedFilterForSave,
  IOpenedFilterData,
  ISelectsData,
} from './models/state'

const composedReportSlice = createSlice({
  name: PARTNER360_COMPOSED_REPORT_NAMESPACE,
  initialState,
  reducers: {
    toggleScopeExpanding: (state) => {
      state.isOpenScopeEditor = !state.isOpenScopeEditor
    },
    closeScopeEditor: (state) => {
      state.isOpenScopeEditor = false
    },
    resetNoPartnersSelectFilters: (state) => {
      state.geo = []
      state.amplifyMembership = []
      state.managers = []
      state.pbms = []
    },
    deleteFromScope: (state, action: PayloadAction<IWWPartners>) => {
      const filtered = state.partnersInScope.filter((p) => p.PartnerID !== action.payload.PartnerID)
      state.partnersInScope = filtered
      state.scopeValue = ScopeSelectKeys.Manual
      composedReportSlice.caseReducers.resetNoPartnersSelectFilters(state)
    },
    setScope: (state, action: PayloadAction<IWWPartners[]>) => {
      const partnersForScope = removeDuplicates(action.payload)
      state.partnersInScope = partnersForScope
    },
    addToScope: (state, action: PayloadAction<IWWPartners[]>) => {
      composedReportSlice.caseReducers.setScope(state, {
        type: action.type,
        payload: [...state.partnersInScope, ...action.payload],
      })
      state.scopeValue = [ScopeSelectKeys.Manual, ScopeSelectKeys.ManualGlobal].includes(
        state.scopeValue as ScopeSelectKeys
      )
        ? state.scopeValue
        : ScopeSelectKeys.Manual
      composedReportSlice.caseReducers.resetNoPartnersSelectFilters(state)
    },

    clearScope: (state) => {
      state.partnersInScope = []
      composedReportSlice.caseReducers.resetNoPartnersSelectFilters(state)
    },
    changeScopeSelectValue: (state, action: PayloadAction<ScopeSelectKeys>) => {
      state.scopeValue = action.payload
      if (
        action.payload === ScopeSelectKeys.Manual ||
        action.payload === ScopeSelectKeys.ManualGlobal
      ) {
        state.isShowScopeSection = true
        state.isOpenScopeEditor = true
        composedReportSlice.caseReducers.resetNoPartnersSelectFilters(state)
      } else {
        state.isShowScopeSection = false
        state.isOpenScopeEditor = false
        state.partnersInScope = []
      }
    },
    changeComposedReportFYearSelectValue: (state, action: PayloadAction<number>) => {
      state.fYear = action.payload
    },
    changeGeoSelectValue: (state, action: PayloadAction<string[]>) => {
      state.geo = action.payload
    },
    changeProductsSelectValue: (state, action: PayloadAction<string[]>) => {
      state.products = action.payload
    },
    changeAcknowledgedSelectValue: (state, action: PayloadAction<string[]>) => {
      state.acknowledged = action.payload
    },
    changeAmplifyMembershipSelectValue: (state, action: PayloadAction<string[]>) => {
      state.amplifyMembership = action.payload
    },
    changePbmSelectValue: (state, action: PayloadAction<string[]>) => {
      state.pbms = action.payload
    },
    changeManagerSelectValue: (state, action: PayloadAction<string[]>) => {
      state.managers = action.payload
    },
    changeIsAcknowledgedValue: (state, action: PayloadAction<boolean>) => {
      state.isAcknowledged = action.payload
    },
    changeSpecializationsSelectValue: (state, action: PayloadAction<string[]>) => {
      state.specializations = action.payload
    },
    changePartnersType: (state, action: PayloadAction<PartnerType>) => {
      state.partnersType = action.payload

      if (state.scopeValue !== ScopeSelectKeys.Manual) {
        state.scopeValue = ScopeSelectKeys.All
      }
    },
    forceToggleGlobalsModal: (state, action: PayloadAction<boolean>) => {
      state.isForceOpenedGlobalsModal = action.payload
    },
    applyFilter: (
      state,
      action: PayloadAction<{ newScope?: IWWPartners[]; appliedFilter: IComposedFilterForReport }>
    ) => {
      state.isFilterApplied = true
      state.isShowScopeSection = true
      state.isOpenScopeEditor = false
      const uniquePartners = action.payload.newScope
        ? removeDuplicates(action.payload.newScope)
        : state.partnersInScope

      const selectsData: ISelectsData = {
        products: state.products,
        partnersInScope: state.partnersInScope,
        scopeValue: state.scopeValue,
        amplifyMembership: state.amplifyMembership,
        geo: state.geo,
        pbms: state.pbms,
        specializations: state.specializations,
        managers: state.managers,
        acknowledged: state.acknowledged,
        fYear: state.fYear,
        partnersType: state.partnersType,
        isAcknowledged: state.isAcknowledged,
      }
      if (state.scopeValue === ScopeSelectKeys.Manual) {
        state.lastAppliedFilter = selectsData //we don't need to set new scope in this case because we already have scope that was selected manually
      } else {
        state.lastAppliedFilter = { ...selectsData, partnersInScope: uniquePartners }
      }
      composedReportSlice.caseReducers.setScope(state, {
        type: action.type,
        payload: uniquePartners,
      })
    },
    clearFilter: (state) => {
      return {
        ...state,
        isShowScopeSection: false,
        isOpenScopeEditor: false,
        isFilterApplied: false,
        ...initialSelectsData,
      }
    },
    setFilter: (state, action: PayloadAction<IComposedReportFilters>) => {
      const item = action.payload
      const filterJson = item.Filters || ''
      const filter: IComposedFilterForSave = filterJson ? JSON.parse(filterJson) : {}
      const scopeValue = filter.Scope.value

      composedReportSlice.caseReducers.updateOpenedFilterData(state, {
        type: action.type,
        payload: { EntryId: item.EntryId, Name: item.Name },
      })

      state.openedFilterData = { EntryId: item.EntryId, Name: item.Name }
      state.scopeValue = scopeValue
      state.products = filter.Products || []
      state.partnersType = filter.PartnersType || PartnerType.Commercial
      state.fYear = filter.FYear

      if (scopeValue === ScopeSelectKeys.Manual || scopeValue === ScopeSelectKeys.ManualGlobal) {
        state.isShowScopeSection = true
        state.isOpenScopeEditor = true

        if (filter.Scope?.partnersInScope?.length) {
          composedReportSlice.caseReducers.setScope(state, {
            type: action.type,
            payload: filter.Scope.partnersInScope,
          })
        }
      } else {
        state.pbms = filter.Pbms || []
        // state.acknowledged = filter.Acknowledged || []
        state.amplifyMembership = filter.AmplifyMembership || []
        state.managers = filter.Managers || []
        state.geo = filter.Geo || []
      }
    },

    updateOpenedFilterData: (state, action: PayloadAction<IOpenedFilterData>) => {
      state.openedFilterData = action.payload
    },
  },
})

export const {
  setScope,
  clearScope,
  addToScope,
  toggleScopeExpanding,
  deleteFromScope,
  changeScopeSelectValue,
  changeAcknowledgedSelectValue,
  changeSpecializationsSelectValue,
  changeAmplifyMembershipSelectValue,
  changeManagerSelectValue,
  changeIsAcknowledgedValue,
  changePbmSelectValue,
  changeComposedReportFYearSelectValue,
  changePartnersType,
  clearFilter,
  setFilter,
  changeProductsSelectValue,
  changeGeoSelectValue,
  applyFilter,
  updateOpenedFilterData,
  forceToggleGlobalsModal,
} = composedReportSlice.actions

export default composedReportSlice.reducer
